import React, { useEffect, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import Select, { components as selectComponents } from "react-select";
import toast from "react-hot-toast";
import moment from "moment";
import DatePicker from "react-multi-date-picker";

import { airportsService } from "../../services/airports.service";
import { airlinesService } from "../../services/airlines.service";
import { fareSearchService } from "../../services/fare-search.service";

const toArray = (rd) => (Array.isArray(rd) ? rd : rd?.data || []);

// const CABIN_CLASSES = ["Economy", "Premium Economy", "Business", "First"];
const CABIN_CLASSES = ["Economy"];

const byText = (get) => (a, b) =>
  String(get(a)).localeCompare(String(get(b)), undefined, {
    sensitivity: "base",
  });

const MAX_PAX = 9;
const ACCENT = "var(--btn-bg)";

const airportSelectStyles = {
  control: (base) => ({
    ...base,
    border: "none",
    boxShadow: "none",
    minHeight: 24,
    fontSize: 14,
    fontWeight: 600,
    background: "transparent",
    cursor: "text",
  }),
  valueContainer: (base) => ({ ...base, padding: 0 }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: () => ({ display: "none" }),
  menu: (base) => ({
    ...base,
    minWidth: 340,
    borderRadius: 10,
    overflow: "hidden",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    cursor: "pointer",
    background: state.isSelected
      ? ACCENT
      : state.isFocused
        ? "#e6f1f8"
        : "#fff",
    color: state.isSelected ? "#fff" : "#1f2937",
  }),
};

const airlineSelectStyles = {
  ...airportSelectStyles,
  control: (base) => ({
    ...base,
    border: "none",
    boxShadow: "none",
    minHeight: 28,
    background: "transparent",
    cursor: "pointer",
  }),
  placeholder: (base) => ({
    ...base,
    margin: 0,
    color: "#111827",
    fontWeight: 700,
    fontSize: 15,
  }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    color: "#111827",
    fontWeight: 700,
    fontSize: 15,
  }),
  indicatorsContainer: () => ({ display: "none" }),
  menu: (base) => ({
    ...base,
    minWidth: 240,
    borderRadius: 10,
    overflow: "hidden",
  }),
};

const AirlineCheckboxOption = (props) => (
  <selectComponents.Option {...props}>
    <div className="d-flex align-items-center gap-2">
      <input
        type="checkbox"
        checked={props.isSelected}
        onChange={() => null}
        style={{ accentColor: ACCENT }}
      />
      <span>{props.label}</span>
    </div>
  </selectComponents.Option>
);

const formatAirportOption = (o, { context }) =>
  context == "menu" ? (
    <div className="d-flex justify-content-between align-items-center gap-3">
      <div>
        <div style={{ fontWeight: 600 }}>
          {o.city_name}, {o.country_name}
        </div>
        <div style={{ fontSize: 11, opacity: 0.75 }}>{o.airport_name}</div>
      </div>
      <span style={{ fontWeight: 700, fontSize: 12 }}>{o.airport_code}</span>
    </div>
  ) : (
    o.city_name
  );

const paxSummary = ({ adults, children, infants }) =>
  [
    `${adults} Adult${adults > 1 ? "s" : ""}`,
    children ? `${children} Child${children > 1 ? "ren" : ""}` : null,
    infants ? `${infants} Infant${infants > 1 ? "s" : ""}` : null,
  ]
    .filter(Boolean)
    .join(", ");

const SEARCH_FORM_KEY = "flight-search-form";
const readFormSnapshot = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SEARCH_FORM_KEY));
  } catch (err) {
    return null;
  }
};

const FlightSearchForm = ({ onSearch, loading, onReset, resetKey, restore }) => {
  const [snap] = useState(() => (restore ? readFormSnapshot() : null));
  const [tripType, setTripType] = useState(snap?.tripType || "one_way");
  const [from, setFrom] = useState(snap?.from || null);
  const [to, setTo] = useState(snap?.to || null);
  const [editing, setEditing] = useState(null); // "from" | "to" | null
  const [departureDate, setDepartureDate] = useState(snap?.departureDate || "");
  const [returnDate, setReturnDate] = useState(snap?.returnDate || "");
  const [airlines, setAirlines] = useState(snap?.airlines || []);
  const [airlineOptions, setAirlineOptions] = useState([]);
  const [pax, setPax] = useState(snap?.pax || { adults: 1, children: 0, infants: 0 });
  const [cabinClass, setCabinClass] = useState(snap?.cabinClass || "Economy");
  const [paxOpen, setPaxOpen] = useState(false);

  const departureRef = useRef(null);
  const returnRef = useRef(null);
  const paxRef = useRef(null);

  const today = moment().format("YYYY-MM-DD");
  // departure_date -> { fare, seats } for the selected From/To route (null = no route chosen yet)
  const [availableDates, setAvailableDates] = useState(null);

  const originCode = from?.airport_code;
  const destinationCode = to?.airport_code;
  useEffect(() => {
    if (!originCode || !destinationCode) {
      setAvailableDates(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fareSearchService.availableDates({
          origin: originCode,
          destination: destinationCode,
        });
        if (cancelled) return;
        const map = {};
        if (res?.success) {
          (Array.isArray(res.resData) ? res.resData : []).forEach((d) => {
            const prev = map[d.departure_date];
            map[d.departure_date] = {
              fare: prev ? Math.min(prev.fare, d.lowest_fare) : d.lowest_fare,
              seats: (prev?.seats || 0) + (d.available_seats || 0),
            };
          });
        } else {
          toast.error(res?.message);
        }
        setAvailableDates(map);
        setDepartureDate((cur) => (cur && !map[cur] ? "" : cur));
      } catch (err) {
        if (!cancelled) setAvailableDates(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [originCode, destinationCode]);

  // calendar day: dates without inventory are disabled, others show the lowest fare
  const mapFareDays = ({ date }) => {
    if (!availableDates) return {};
    const info = availableDates[date.format("YYYY-MM-DD")];
    if (!info) return { disabled: true };
    return {
      children: (
        <div className="fs-day">
          <div className="fs-day-n">{date.day}</div>
          <div className="fs-day-fare">
            &#8377;{Number(info.fare).toLocaleString("en-IN")}
          </div>
        </div>
      ),
    };
  };
  const isRoundTrip = tripType == "round_trip";

  // The page's Reset button bumps resetKey: clear every field
  useEffect(() => {
    if (!resetKey) return;
    setTripType("one_way");
    setFrom(null);
    setTo(null);
    setEditing(null);
    setDepartureDate("");
    setReturnDate("");
    setAirlines([]);
    setPax({ adults: 1, children: 0, infants: 0 });
    setCabinClass("Economy");
    setPaxOpen(false);
  }, [resetKey]);

  // Airline dropdown: airlines > list (status = active)
  useEffect(() => {
    (async () => {
      try {
        const res = await airlinesService.list({ page: 1, status: "active" });
        if (res?.success) {
          setAirlineOptions(
            toArray(res.resData)
              .map((a) => ({
                value: a.airline_code,
                label: a.airline_name + " (" + a.airline_code + ")",
                airline_code: a.airline_code,
              }))
              .sort(byText((o) => o.label)),
          );
        }
      } catch (err) {
        setAirlineOptions([]);
      }
    })();
  }, []);

  // Close the passengers popover on an outside click
  useEffect(() => {
    if (!paxOpen) return;
    const handler = (e) => {
      if (paxRef.current && !paxRef.current.contains(e.target))
        setPaxOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [paxOpen]);

  // From / To dropdowns: airports > list (status = active), searched on the server
  const loadAirports = async (search = "") => {
    try {
      const searchText = search.trim();

      // Skip API call for 1 or 2 characters
      if (searchText.length > 0 && searchText.length < 3) {
        return [];
      }

      const params = {
        page: 1,
        limit: 20,
        is_popular: true,
        status: "active",
        ...(searchText.length === 3
          ? { iata_id: searchText }
          : searchText.length > 3
            ? { search: searchText }
            : {}),
      };

      const res = await airportsService.list(params);

      if (res?.success) {
        return toArray(res.resData)
          .map((a) => ({
            value: a.airport_code,
            airport_code: a.airport_code,
            airport_name: a.airport_name,
            city_name: a.city_name,
            country_name: a.country_name,
          }))
          .sort(byText((o) => o.city_name));
      }

      toast.error(res?.message);
      return [];
    } catch (err) {
      return [];
    }
  };

  const openPicker = (ref) => {
    ref.current?.openCalendar();
  };

  const handleTripType = (type) => {
    setTripType(type);
    if (type == "one_way") {
      setReturnDate("");
    } else if (!returnDate) {
      setReturnDate(departureDate); // round trip starts on the departure date
    }
  };

  const handleDepartureChange = (value) => {
    setDepartureDate(value);
    if (
      isRoundTrip &&
      value &&
      (!returnDate || moment(returnDate).isBefore(value))
    )
      setReturnDate(value);
  };

  const handleSwap = () => {
    setFrom(to);
    setTo(from);
  };

  // Adults + children are capped at 9, and each infant needs an adult
  const changePax = (key, delta) => {
    setPax((prev) => {
      const next = { ...prev, [key]: prev[key] + delta };
      if (next.adults < 1 || next.children < 0 || next.infants < 0) return prev;
      if (next.adults + next.children > MAX_PAX) return prev;
      if (next.infants > next.adults) {
        if (key == "infants") return prev;
        next.infants = next.adults;
      }
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!from || !to)
      return toast.error("Please select both From and To airports");
    if (from.airport_code == to.airport_code)
      return toast.error("From and To airports cannot be the same");
    if (!departureDate) return toast.error("Please select a departure date");
    if (isRoundTrip && !returnDate)
      return toast.error("Please select a return date for a round trip");

    const payload = {
      trip_type: tripType,
      route_type:
        from.country_name &&
        to.country_name &&
        from.country_name != to.country_name
          ? "international"
          : "domestic",
      nearby_airport: true,
      ...(airlines.length
        ? { airlines: airlines.map((a) => a.airline_code) }
        : {}),
      sector: {
        origin: from.airport_code,
        destination: to.airport_code,
        departure_date: departureDate,
        departure_time_from: "00:01",
        departure_time_to: "23:59",
        cabin_class: cabinClass,
        ...(isRoundTrip ? { return_date: returnDate } : {}),
      },
      pax_detail: { ...pax },
    };

    try {
      sessionStorage.setItem(SEARCH_FORM_KEY, JSON.stringify({ tripType, from, to, departureDate, returnDate, airlines, pax, cabinClass }));
    } catch (err) {
      // storage unavailable: the form just will not be restored
    }
    onSearch(payload);
  };

  const renderAirportBox = (key, label, value, setValue) => (
    <div className="fs-box fs-airport" onClick={() => setEditing(key)}>
      <div className="fs-label">{label}</div>
      {editing == key ? (
        <AsyncSelect
          autoFocus
          defaultMenuIsOpen
          cacheOptions
          defaultOptions
          loadOptions={loadAirports}
          value={value}
          onChange={(opt) => {
            if (opt) setValue(opt);
            setEditing(null);
          }}
          onBlur={() => setEditing(null)}
          formatOptionLabel={formatAirportOption}
          getOptionValue={(o) => o.airport_code}
          placeholder="Search city or airport"
          noOptionsMessage={() => "No airports found"}
          styles={airportSelectStyles}
          menuPortalTarget={document.body}
        />
      ) : (
        <div className="fs-value">{value?.city_name || "Select city"}</div>
      )}
      <div className="fs-sub text-truncate">
        {value ? `${value.airport_code}, ${value.airport_name}` : " "}
      </div>
    </div>
  );

  const renderDate = (value) => {
    const m = moment(value);
    return (
      <>
        <div className="fs-value">
          {m.format("D")} <span className="fs-month">{m.format("MMM'YY")}</span>
        </div>
        <div className="fs-sub fs-sub-dark">{m.format("dddd")}</div>
      </>
    );
  };

  const paxRow = (key, title, hint) => (
    <div
      className="d-flex align-items-center justify-content-between py-2"
      key={key}
    >
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#1f2937" }}>
          {title}
        </div>
        <div style={{ fontSize: 11, color: "#475569" }}>{hint}</div>
      </div>
      <div className="d-flex align-items-center gap-2">
        <button
          type="button"
          className="fs-counter"
          onClick={() => changePax(key, -1)}
        >
          <i className="bi bi-dash"></i>
        </button>
        <span style={{ width: 20, textAlign: "center", fontWeight: 700 }}>
          {pax[key]}
        </span>
        <button
          type="button"
          className="fs-counter"
          onClick={() => changePax(key, 1)}
        >
          <i className="bi bi-plus"></i>
        </button>
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      <style>{formCss}</style>

      {/* Trip type + reset */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <div className="fs-trip-toggle" role="group" aria-label="Trip type">
          <button
            type="button"
            className={`fs-trip ${tripType == "one_way" ? "active" : ""}`}
            onClick={() => handleTripType("one_way")}
          >
            One Way
          </button>
          <button
            type="button"
            className={`fs-trip ${isRoundTrip ? "active" : ""}`}
            onClick={() => handleTripType("round_trip")}
          >
            Round Trip
          </button>
        </div>
        <button type="button" className="fs-reset" onClick={onReset}>
          <i className="bi bi-arrow-counterclockwise"></i> Reset
        </button>
      </div>

      {/* Main search bar */}
      <div className="fs-bar">
        <div className="fs-route">
          {renderAirportBox("from", "From", from, setFrom)}
          <button
            type="button"
            className="fs-swap"
            onClick={handleSwap}
            title="Swap"
            aria-label="Swap From and To"
          >
            <i className="bi bi-arrow-left-right"></i>
          </button>
          {renderAirportBox("to", "To", to, setTo)}
        </div>

        <div
          className="fs-box fs-date"
          onClick={() => openPicker(departureRef)}
        >
          <div className="fs-label">Departure</div>
          {departureDate ? (
            renderDate(departureDate)
          ) : (
            <div className="fs-hint">Select date</div>
          )}
          <div className="fs-hidden-date" onClick={(e) => e.stopPropagation()}>
            <DatePicker
              onOpenPickNewDate={false}
              ref={departureRef}
              value={departureDate || null}
              onChange={(d) =>
                handleDepartureChange(d ? d.format("YYYY-MM-DD") : "")
              }
              format="YYYY-MM-DD"
              minDate={today}
              editable={false}
              weekStartDayIndex={1}
              className="fs-fare-calendar"
              mapDays={mapFareDays}
              inputClass="fs-hidden-date-input"
            />
          </div>
        </div>

        <div
          className={`fs-box fs-date ${isRoundTrip ? "" : "fs-disabled"}`}
          onClick={() => isRoundTrip && openPicker(returnRef)}
          aria-disabled={!isRoundTrip}
        >
          <div className="fs-label">Return</div>
          {isRoundTrip && returnDate ? (
            renderDate(returnDate)
          ) : (
            <div className="fs-hint">
              {isRoundTrip
                ? "Select return date"
                : "Select Round Trip to add a return date"}
            </div>
          )}
          <div className="fs-hidden-date" onClick={(e) => e.stopPropagation()}>
            <DatePicker
              onOpenPickNewDate={false}
              ref={returnRef}
              value={returnDate || null}
              onChange={(d) => setReturnDate(d ? d.format("YYYY-MM-DD") : "")}
              format="YYYY-MM-DD"
              minDate={departureDate || today}
              editable={false}
              disabled={!isRoundTrip}
              inputClass="fs-hidden-date-input"
            />
          </div>
        </div>

        {/* <div className="fs-box fs-airline">
          <div className="fs-label">Airline</div>
          <Select
            value={airlines}
            onChange={(opts) => setAirlines(opts || [])}
            options={airlineOptions}
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            controlShouldRenderValue={false}
            components={{ Option: AirlineCheckboxOption }}
            placeholder={
              airlines.length
                ? airlines.length <= 2
                  ? airlines.map((a) => a.airline_code).join(", ")
                  : airlines.length + " Airlines"
                : "All Airlines"
            }
            noOptionsMessage={() => "No airlines found"}
            styles={airlineSelectStyles}
            menuPortalTarget={document.body}
            aria-label="Airline"
          />
        </div> */}

        <div className="fs-box fs-pax" ref={paxRef}>
          <div
            onClick={() => setPaxOpen(!paxOpen)}
            style={{ cursor: "pointer" }}
          >
            <div className="fs-label">Passengers &amp; Class</div>
            <div className="d-flex justify-content-between align-items-center">
              <div className="fs-value fs-value-sm text-truncate">
                {paxSummary(pax)}
              </div>
              <i
                className={`bi bi-chevron-${paxOpen ? "up" : "down"}`}
                style={{ color: "#475569", fontSize: 12 }}
              ></i>
            </div>
            <div className="fs-sub">{cabinClass}</div>
          </div>

          {paxOpen && (
            <div className="fs-pax-pop">
              {paxRow("adults", "Adults", "12 yrs & above")}
              {paxRow("children", "Children", "2 - 12 yrs")}
              {paxRow("infants", "Infants", "Below 2 yrs")}
              <div className="fs-label mt-3 mb-2">Travel Class</div>
              <div className="d-flex flex-wrap gap-2">
                {CABIN_CLASSES.map((c) => (
                  <button
                    type="button"
                    key={c}
                    className={`fs-class ${cabinClass == c ? "active" : ""}`}
                    onClick={() => setCabinClass(c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div className="text-end mt-3">
                <button
                  type="button"
                  className="btn btn-sm px-4 fs-done"
                  onClick={() => setPaxOpen(false)}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>

        <button type="submit" className="fs-search" disabled={loading}>
          {loading ? (
            <span className="spinner-border spinner-border-sm" />
          ) : (
            <>
              SEARCH <i className="bi bi-search"></i>
            </>
          )}
        </button>
      </div>
    </form>
  );
};

const formCss = `
.fs-trip-toggle { display:inline-flex; align-items:center; gap:2px; padding:3px; border-radius:8px; background:#f3f4fb; border:1px solid #d5dbe8; }
.fs-trip { border:none; background:transparent; color:#4b5563; font-weight:600; font-size:13px; padding:5px 14px; border-radius:6px; white-space:nowrap; }
.fs-trip:hover { color:${ACCENT}; }
.fs-trip.active { background:var(--btn-bg); color:#fff; }
.fs-reset { border:none; background:none; color:#6b7280; font-size:13px; font-weight:600; padding:0; display:flex; align-items:center; gap:5px; }
.fs-reset:hover { color:${ACCENT}; }

.fs-bar { display:flex; background:#fff; border-radius:10px; border:1px solid #d5dbe8; overflow:visible; }
.fs-route { display:flex; flex:3.2; position:relative; min-width:0; }
.fs-box { position:relative; padding:12px 18px; border-right:1px solid #eceff4; min-width:0; min-height:84px; cursor:pointer; transition:background .15s; }
.fs-box:hover { background:#f4fbf9; }
.fs-airport { flex:1; }
.fs-swap + .fs-airport { padding-left:30px; }
.fs-date { flex:.85; }
.fs-airline { flex:1; }
.fs-pax { flex:1.05; cursor:default; }
.fs-disabled { cursor:not-allowed; background:#f8f9fb; }
.fs-disabled:hover { background:#f8f9fb; }
.fs-disabled .fs-hint { color:#6b7280; font-weight:500; }
.fs-label { font-size:11px; font-weight:600; letter-spacing:.4px; text-transform:uppercase; color:#4b5563; margin-bottom:2px; }
.fs-value { font-size:18px; font-weight:700; color:#111827; line-height:1.3; }
.fs-value-sm { font-size:15px; }
.fs-month { font-size:13px; font-weight:600; }
.fs-sub { font-size:12px; color:#6b7280; margin-top:1px; }
.fs-sub-dark { color:#374151; font-size:13px; }
.fs-hint { font-size:13px; font-weight:600; color:${ACCENT}; line-height:1.4; }
.fs-fare-calendar .rmdp-day { height:50px; width:54px; }
.fs-fare-calendar .rmdp-day > span { display:flex; align-items:center; justify-content:center; border-radius:8px; left:1px; right:1px; top:1px; bottom:1px; }
.fs-fare-calendar .rmdp-day .fs-day { display:flex; flex-direction:column; align-items:center; justify-content:center; line-height:1.2; }
.fs-fare-calendar .rmdp-day .fs-day .fs-day-n { font-size:13px; font-weight:600; color:#111827 !important; }
.fs-fare-calendar .rmdp-day .fs-day .fs-day-fare { font-size:9px; font-weight:600; color:var(--btn-bg) !important; white-space:nowrap; }
.fs-fare-calendar .rmdp-day.rmdp-today > span:not(.highlight) { background:transparent !important; color:inherit; border:1px solid #cfd8e3; }
.fs-fare-calendar .rmdp-day.rmdp-selected > span:not(.highlight) { background:#e6f1f8 !important; border:1px solid #9fd3c9; box-shadow:none !important; color:inherit; }
.fs-fare-calendar .rmdp-day.rmdp-selected .fs-day-n { color:var(--btn-bg) !important; font-weight:700; }
.fs-fare-calendar .rmdp-day.rmdp-deactive .fs-day-n, .fs-fare-calendar .rmdp-day.rmdp-deactive .fs-day-fare { opacity:.6; }
.fs-fare-calendar .rmdp-day.rmdp-disabled { color:#c3cad8 !important; }
.fs-hidden-date { position:absolute; left:18px; bottom:10px; width:1px; height:1px; }
.fs-hidden-date-input { width:1px; height:1px; opacity:0; padding:0; border:none; pointer-events:none; }
.fs-swap { position:absolute; left:50%; top:50%; transform:translate(-50%,-50%); z-index:2; width:34px; height:34px; border-radius:50%;
  border:1px solid #a7d8d0; background:#fff; color:${ACCENT}; font-size:14px; display:flex; align-items:center; justify-content:center; padding:0; }
.fs-swap:hover { background:#e6f1f8; }
.fs-search { border:none; background:var(--btn-bg); color:#fff; font-weight:700; font-size:15px; letter-spacing:1px; padding:0 32px;
  border-radius:0 9px 9px 0; display:flex; align-items:center; justify-content:center; gap:10px; min-width:150px; transition:background .15s; }
.fs-search:hover:not(:disabled) { background:var(--btn-hover-bg); }
.fs-search:focus-visible, .fs-trip:focus-visible, .fs-swap:focus-visible { outline:2px solid ${ACCENT}; outline-offset:2px; }
.fs-search:disabled { opacity:.8; }

.fs-pax-pop { position:absolute; top:calc(100% + 8px); right:0; width:320px; background:#fff; border-radius:12px; padding:16px;
  box-shadow:0 12px 32px rgba(0,0,0,.18); z-index:1500; cursor:default; }
.fs-counter { width:30px; height:30px; border-radius:50%; border:1px solid #a7d8d0; background:#fff; color:${ACCENT};
  display:flex; align-items:center; justify-content:center; }
.fs-counter:hover { background:#e6f1f8; }
.fs-class { border:1px solid #e5e7eb; background:#fff; border-radius:999px; font-size:12px; padding:5px 12px; color:#374151; }
.fs-class.active { border-color:${ACCENT}; background:#e6f1f8; color:${ACCENT}; font-weight:600; }
.fs-done { background:var(--btn-bg); color:#fff; border-radius:8px; height:34px; font-size:13px; }
.fs-done:hover { background:var(--btn-hover-bg); color:#fff; }

@media (max-width: 991px) {
  .fs-bar { flex-wrap:wrap; }
  .fs-route { flex:1 1 100%; border-bottom:1px solid #eceff4; }
  .fs-date, .fs-airline, .fs-pax { flex:1 1 30%; border-bottom:1px solid #eceff4; }
  .fs-search { flex:1 1 100%; border-radius:0 0 9px 9px; padding:12px; min-height:46px; }
}
@media (max-width: 575px) {
  .fs-trip-toggle { width:100%; }
  .fs-trip { flex:1; padding:5px 6px; font-size:12px; }
  .fs-route { flex-direction:column; }
  .fs-airport { border-right:none; border-bottom:1px solid #eceff4; }
  .fs-swap + .fs-airport { padding-left:18px; }
  .fs-swap { left:auto; right:16px; transform:translateY(-50%) rotate(90deg); }
  .fs-date, .fs-airline { flex:1 1 50%; }
  .fs-pax { flex:1 1 100%; border-right:none; }
  .fs-pax-pop { width:100%; }
}
`;

export default FlightSearchForm;
